import { ref } from 'vue'

// Tipos de log
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

// Interfaz para el contexto del log
export interface LogContext {
  component?: string
  method?: string
  data?: Record<string, unknown>
  timestamp?: number
}

// Interfaz para el error personalizado
export interface AppError extends Error {
  code: string
  context?: LogContext
  originalError?: Error
}

// Clase para errores personalizados
export class CustomError extends Error implements AppError {
  code: string
  context?: LogContext
  originalError?: Error

  constructor(message: string, code: string, context?: LogContext, originalError?: Error) {
    super(message)
    this.name = 'CustomError'
    this.code = code
    this.context = context
    this.originalError = originalError
  }
}

export function useLogger() {
  const logs = ref<
    Array<{ level: LogLevel; message: string; context?: LogContext; timestamp: number }>
  >([])
  const errors = ref<Array<AppError>>([])
  const isDevelopment = import.meta.env.VITE_DEV === 'true'

  // Función para formatear el mensaje de log
  const formatLogMessage = (level: LogLevel, message: string, context?: LogContext): string => {
    const timestamp = new Date().toISOString()
    const contextStr = context
      ? ` [${context.component}${context.method ? `.${context.method}` : ''}]`
      : ''
    return `[${timestamp}] ${level}${contextStr}: ${message}`
  }

  // Función para registrar un log
  const log = (level: LogLevel, message: string, context?: LogContext) => {
    const timestamp = Date.now()
    const logEntry = { level, message, context, timestamp }
    logs.value.push(logEntry)

    // Solo mostrar logs en consola en desarrollo o errores
    if (isDevelopment || level === LogLevel.ERROR) {
      const formattedMessage = formatLogMessage(level, message, context)
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(formattedMessage, context?.data)
          break
        case LogLevel.INFO:
          console.info(formattedMessage, context?.data)
          break
        case LogLevel.WARN:
          console.warn(formattedMessage, context?.data)
          break
        case LogLevel.ERROR:
          console.error(formattedMessage, context?.data)
          break
      }
    }
  }

  // Funciones de conveniencia para diferentes niveles de log
  const debug = (message: string, context?: LogContext) => log(LogLevel.DEBUG, message, context)
  const info = (message: string, context?: LogContext) => log(LogLevel.INFO, message, context)
  const warn = (message: string, context?: LogContext) => log(LogLevel.WARN, message, context)
  const error = (message: string, context?: LogContext) => log(LogLevel.ERROR, message, context)

  // Función para manejar errores
  const handleError = (error: Error | string, context?: LogContext): AppError => {
    let appError: AppError

    if (typeof error === 'string') {
      appError = new CustomError(error, 'UNKNOWN_ERROR', context)
    } else if (error instanceof CustomError) {
      appError = error
    } else {
      appError = new CustomError(error.message, 'UNKNOWN_ERROR', context, error)
    }

    errors.value.push(appError)
    log(LogLevel.ERROR, appError.message, {
      ...context,
      data: {
        code: appError.code,
        originalError: appError.originalError,
      },
    })

    return appError
  }

  // Función para crear un error personalizado
  const createError = (
    message: string,
    code: string,
    context?: LogContext,
    originalError?: Error,
  ): AppError => {
    return new CustomError(message, code, context, originalError)
  }

  // Función para limpiar logs y errores
  const clear = () => {
    logs.value = []
    errors.value = []
  }

  // Función para obtener logs filtrados
  const getLogs = (level?: LogLevel) => {
    return level ? logs.value.filter(log => log.level === level) : logs.value
  }

  // Función para obtener errores
  const getErrors = () => errors.value

  return {
    debug,
    info,
    warn,
    error,
    handleError,
    createError,
    clear,
    getLogs,
    getErrors,
    LogLevel,
  }
}
